import { Component, computed, inject, input, output } from '@angular/core';

import { MDContentBoxComponent, MDIconComponent, MDTagComponent, MDToolTipDirective } from '@melodic-ui/components';
import { IndicatorType } from '@melodic-ui/types';
import { GradeStateService } from '@lb/state/grade-state.service';
import { IEnrollmentDetailItem, ISemesterDetail } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { ICoreGrade } from '@lb/api/core/interfaces';
import { GradeTagComponent } from '@lb/components';
import { IGradingTemplateDetail } from '@lb/api/core/interfaces/igrading-template.interface';

interface GradeCellDisplay {
	gradeName: string | null;
	hasGrades: boolean;
	/** True when the shown grade is an override; calculatedGradeName is what it replaced. */
	isOverridden: boolean;
	calculatedGradeName: string | null;
	/** Core.Grades.Color of the shown grade, as set on the grade scale — the colour every other grade surface uses. */
	color: IndicatorType;
}

interface SemesterRowDisplay {
	semesterNumber: number;
	levelName: string;
	categoryGrades: Record<number, GradeCellDisplay>;
	finalGrade: GradeCellDisplay;
	/** The row's source, handed back through editSemesterGrades so the host can open the dialog on it. */
	semester: ISemesterDetail;
	/**
	 * Every lesson in the semester is Completed — the same rule that raises the instructor's
	 * FinalGradeSubmissionRequired prompt, and the condition under which the API accepts a grade.
	 */
	lessonsComplete: boolean;
}

interface CategoryColumn {
	categoryID: number;
	categoryName: string;
}

const EMPTY_CELL: GradeCellDisplay = { hasGrades: false, gradeName: null, isOverridden: false, calculatedGradeName: null, color: 'default' };

@Component({
	selector: 'lb-final-grades-summary',
	standalone: true,
	imports: [MDContentBoxComponent, MDIconComponent, MDTagComponent, MDToolTipDirective, GradeTagComponent],
	templateUrl: './final-grades-summary.component.html',
	styleUrl: './final-grades-summary.component.scss'
})
export class FinalGradesSummaryComponent {
	private _gradeState = inject(GradeStateService);

	enrollmentDetail = input.required<IEnrollmentDetailItem>();
	gradingTemplate = input.required<IGradingTemplateDetail>();

	/**
	 * Host-supplied capability, default off: adds an Edit action per semester and marks overridden
	 * finals. The admin Enrollment Summary turns it on for holders of Admin:Grade:Edit; the instructor
	 * and learner apps never pass it, so their rendering is unchanged.
	 */
	canEdit = input<boolean>(false);

	/**
	 * The semester whose Edit action was clicked. Only reachable when canEdit is on. The host
	 * opens the Review & Submit Grades dialog on it — the same dialog the instructor submits from.
	 */
	editSemesterGrades = output<ISemesterDetail>();

	constructor() {
		this._gradeState.loadGradeScales();
	}

	private _grade(grades: ICoreGrade[], gradeID: number | null | undefined): ICoreGrade | undefined {
		return grades.find((g) => g.gradeID === gradeID);
	}

	private _cell(grades: ICoreGrade[], calculatedID: number, overriddenID: number | null | undefined, isOverridden: boolean): GradeCellDisplay {
		const calculated = this._grade(grades, calculatedID);
		// The colour follows the grade on show, so an overridden final takes the override's colour.
		const shown = isOverridden ? this._grade(grades, overriddenID) : calculated;
		return {
			hasGrades: true,
			isOverridden,
			calculatedGradeName: calculated?.gradeName ?? null,
			gradeName: shown?.gradeName ?? null,
			color: shown?.color ?? 'default'
		};
	}

	// Category columns always come from the grading template, ordered by orderNumber
	categoryColumns = computed<CategoryColumn[]>(() => {
		const template = this.gradingTemplate();
		if (!template?.categories?.length) return [];

		return [...template.categories]
			.sort((a, b) => a.orderNumber - b.orderNumber)
			.map((c) => ({ categoryID: c.gradingTemplateCategoryID, categoryName: c.categoryName }));
	});

	semesters = computed<SemesterRowDisplay[]>(() => {
		const detail = this.enrollmentDetail();
		// The template owns the scale, so names resolve against that scale rather than a global list.
		const grades = this._gradeState.getGradeScaleByID(this.gradingTemplate().gradeScaleID)?.grades ?? [];

		if (!detail?.semesters?.length) return [];

		return detail.semesters.map((s: ISemesterDetail): SemesterRowDisplay => {
			// Per-category grades
			const categoryGrades: Record<number, GradeCellDisplay> = {};
			for (const sg of s.semesterGrades ?? []) {
				categoryGrades[sg.gradingTemplateCategoryID] = this._cell(grades, sg.overallCalculatedGradeID, sg.overridenGradeID, !!sg.isOverriden);
			}

			// Overall final grade
			const final = s.semesterFinalGrade;
			const finalGrade: GradeCellDisplay = final ? this._cell(grades, final.overallCalculatedGradeID, final.overridenGradeID, !!final.isOverriden) : EMPTY_CELL;

			const expected = s.expectedLessonCount ?? 0;
			const completed = s.completedLessonCount ?? 0;

			return {
				semesterNumber: s.semesterNumber,
				levelName: s.levelName,
				categoryGrades,
				finalGrade,
				semester: s,
				lessonsComplete: expected > 0 && completed >= expected
			};
		});
	});

	// The semester label column is only useful when there is more than one semester to distinguish.
	showSemesterColumn = computed(() => this.semesters().length > 1);

	gridTemplateColumns = computed(() => {
		// (optional) semester label sized to its content (auto), then one column per category + final column,
		// then (optional) an actions column sized to its button. Equal-width tracks (minmax(0, 1fr)) so the
		// data columns fill the width and space evenly.
		const categoryCount = this.categoryColumns().length;
		const dataColumns = `repeat(${categoryCount + 1}, minmax(0, 1fr))`;
		const columns = this.showSemesterColumn() ? `auto ${dataColumns}` : dataColumns;
		return this.canEdit() ? `${columns} auto` : columns;
	});

	// Several rows share the visible "Edit" text, so the accessible name carries the semester.
	rowLabel(row: SemesterRowDisplay): string {
		return row.levelName || `Semester ${row.semesterNumber}`;
	}
}
